import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { useCourses } from '@hooks';
import { useAuth } from '@context';
import { getManagedCourses, isAdmin } from '@utils/permissions';
import { CourseEmailTemplateManager } from '@components/admin/CourseEmailTemplateManager';
import { ROUTES } from '@config/routes';

export function EmailsPage() {
  const { courses, loading, error } = useCourses();
  const { user } = useAuth();
  const userIsAdmin = isAdmin(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const scopedCourses = useMemo(
    () => getManagedCourses(user, courses),
    [courses, user]
  );

  const requestedCourseId = searchParams.get('courseId');

  useEffect(() => {
    if (!scopedCourses.length) {
      setSelectedCourseId('');
      return;
    }

    if (requestedCourseId && scopedCourses.some((course) => course.id === requestedCourseId)) {
      setSelectedCourseId(requestedCourseId);
      return;
    }

    if (selectedCourseId && scopedCourses.some((course) => course.id === selectedCourseId)) {
      return;
    }

    setSelectedCourseId(scopedCourses[0].id);
  }, [requestedCourseId, scopedCourses, selectedCourseId]);

  const selectedCourse = useMemo(
    () => scopedCourses.find((course) => course.id === selectedCourseId) || null,
    [scopedCourses, selectedCourseId]
  );

  const handleCourseChange = (event) => {
    const nextCourseId = event.target.value;
    setSelectedCourseId(nextCourseId);

    const nextParams = new URLSearchParams(searchParams);
    if (nextCourseId) {
      nextParams.set('courseId', nextCourseId);
    } else {
      nextParams.delete('courseId');
    }

    setSearchParams(nextParams, { replace: true });
  };

  if (loading) {
    return <div className="text-center py-5">Loading email templates...</div>;
  }

  return (
    <div>
      <div className="mb-4 d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <h4 className="mb-1">Course Email Templates</h4>
          <small className="text-muted">
            Customize student-facing emails by course for enrollment, account, reminder, and password reset flows.
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to={ROUTES.ADMIN_EMAIL_LOGS} variant="outline-secondary" size="sm">
            View Email Logs
          </Button>
          {userIsAdmin && (
            <Button as={Link} to={ROUTES.ADMIN_EMAIL_CONFIG} variant="outline-primary" size="sm">
              Email Configuration
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={8}>
              <Form.Group>
                <Form.Label>Select Course</Form.Label>
                <Form.Select value={selectedCourseId} onChange={handleCourseChange}>
                  {scopedCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={4}>
              <div className="small text-muted">
                Best practices: keep subjects concise, avoid passwords in email, and include one clear call to action.
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!scopedCourses.length ? (
        <Alert variant="info" className="mb-0">
          No managed courses are available for email customization.
        </Alert>
      ) : (
        <CourseEmailTemplateManager
          courseId={selectedCourse?.id}
          courseTitle={selectedCourse?.title}
        />
      )}
    </div>
  );
}
